<!-- eslint-disable vue/component-name-in-template-casing -->
<template>
  <ContentSection
    class="overflow-hidden transition-opacity duration-1000 ease-out"
    :class="isReady ? `opacity-100` : `opacity-0`"
  >
    <AnimatedContainer
      v-if="hasSlider"
      class="mx-[-91vw] lg:mx-[-37vw] md:mx-[-55vw] sm:mx-[-70vw] xs:mx-[-79vw]"
    >
      <swiper-container
        ref="sliderRef"
        class="flex"
      >
        <swiper-slide v-if="!hasMoreThanThreeSlides" />
        <swiper-slide
          v-for="item, index in slidesData"
          :key="item.id"
          :ref="(el) => slides[index] = el"
        >
          <figure
            class="pb-8 transition-opacity duration-300 ease-in zoom-in"
            :class="
              isReady ? `opacity-100` : `opacity-0`"
            :style="`transition-delay: ${(index) * 180}ms;`"
          >
            <AppImage
              :src="item.image"
              class="aspect-video max-h-180 bg-grey-100 object-cover"
              :class="{ 'w-full': isReady }"
            />
            <figcaption
              v-if="item.caption"
              class="absolute bottom-0 left-0 text-grey-300 typo-subtitle"
            >
              {{ item.caption }}
            </figcaption>
          </figure>
        </swiper-slide>
        <swiper-slide v-if="!hasMoreThanThreeSlides" />
      </swiper-container>
    </AnimatedContainer>
    <AnimatedContainer v-else>
      <Container tag="div">
        <figure class="zoom-in relative pb-8">
          <AppImage
            :src="slidesData[0].image"
            class="aspect-[3/2] w-full max-w-prose bg-grey-100 object-cover"
          />
          <figcaption
            v-if="slidesData[0].caption"
            class="absolute bottom-0 left-0 text-grey-300 typo-subtitle"
          >
            {{ slidesData[0].caption }}
          </figcaption>
        </figure>
      </Container>
    </AnimatedContainer>
    <!--
      The formula for following section is:
      mr = (100vw + 2 * [slider-container-wrapper-mx]) + ([slider-gap] / 2) - [slider-container-wrapper-mx]
    -->
    <div
      v-if="hasSlider"
      class="
        relative z-10 -mt-6 mr-[calc(94vw+16px-91vw-10px)]
        lg:mr-[calc(58vw+16px-37vw-10px)]
        md:mr-[calc(70vw+12px-55vw-10px)]
        sm:mr-[calc(80vw+8px-70vw-10px)]
        xs:mr-[calc(86vw+8px-79vw-10px)]
      "
    >
      <div class="flex w-full items-start justify-end gap-8">
        <div class="text-grey-300 typo-subtitle">
          {{ activeIndex + 1 }}/{{ slidesData.length }}
        </div>
        <div class="flex gap-2 pt-0.5">
          <button type="button" @click="swiper.prev()">
            <IconArrowLeft class="size-5" />
          </button>
          <button type="button" @click="swiper.next()">
            <IconArrowRight class="size-5" />
          </button>
        </div>
      </div>
    </div>
  </ContentSection>
</template>

<script setup lang="ts">
import type { MainComponentsImageGalleryFragment } from '#gql'

const props = defineProps<{
  data: Enforce<MainComponentsImageGalleryFragment>
}>()

const sliderRef = ref(null)
const slides = ref<Element[] | ComponentPublicInstance[] | null[]>([])
const activeIndex = ref(0)

const hasSlider = computed(() => props.data.items.length > 1)
const hasMoreThanThreeSlides = computed(() => props.data.items.length > 3)

const isReady = ref(false)

const slidesData = computed(() => {
  if (!hasMoreThanThreeSlides.value) {
    return props.data.items
  }

  const items = [...props.data.items]
  const lastItem = items.pop()
  if (lastItem) {
    items.unshift(lastItem)
  }
  return items
})

const swiper = useSwiper(sliderRef, {
  slidesPerView: 3,
  loop: hasMoreThanThreeSlides.value,
  breakpoints: {
    0: {
      spaceBetween: 16,
    },
    768: {
      spaceBetween: 24,
    },
    1024: {
      spaceBetween: 32,
    },
  },
})

onMounted(async () => {
  await nextTick()
  if (hasSlider.value) {
    const swiperInstance = swiper.instance.value

    if (swiperInstance) {
      swiperInstance.on('slideChange', () => {
        activeIndex.value = swiperInstance.realIndex
      })
    }
  }
  isReady.value = true
})
</script>
